import { marketMapGeneration } from "@/shared/utils/map";
import { PDF_GENERATION_ERROR_MESSAGE } from "@constants/index";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGeneratePdfMutation,
  useGetPdfUrlQuery,
  useUploadMarketMapFileMutation,
} from "@services/media";
import { getVisibleProperties } from "@shared/utils/propertyFiltering";
import Analytics from "@shared/utils/tagManagerAnalytics";
import type {
  MediaFile,
  PdfFileInfo,
  PdfGenerationOptions,
  PdfMediaFile,
  PdfSection,
} from "@survey-types/survey.media.types";
import type { SurveySection } from "@survey-types/survey.section.types";
import type {
  PdfStatus,
  UseSurveyPDFOptions,
  UseSurveyPDFReturn,
} from "@survey-types/survey.share.types";
import dayjs from "dayjs";
import { useEffect, useEffectEvent, useRef, useState } from "react";

/** Date utilities for PDF generation */
export const dateUtils = {
  /** Ensures dates are in proper ISO format */
  normalize: (date: string): string => (date.endsWith("Z") ? date : `${date}Z`),

  /** Formats survey update timestamps for display */
  formatSurveyUpdate: (updatedOn: string | undefined) => {
    if (!updatedOn) return null;
    const date = dayjs(dateUtils.normalize(updatedOn));
    return {
      date: date.format("MM/DD/YYYY"),
      time: date.format("hh:mm A"),
    };
  },
};

// Backward compatibility exports
export const normalizeDate = dateUtils.normalize;
export const formatSurveyUpdateDate = dateUtils.formatSurveyUpdate;

/**
 * Creates base survey sections with their preview URLs
 */
export const createBaseSections = (
  domain: string,
  surveyId: string,
): Record<string, string> => ({
  cover: `${domain}/${surveyId}/preview/cover`,
  "property comparison": `${domain}/${surveyId}/preview/propertycomparison`,
  "lease comps comparison": `${domain}/${surveyId}/preview/leasecompscomparison`,
  "market map": `${domain}/${surveyId}/preview/marketmap`,
  "tour schedule": `${domain}/${surveyId}/preview/tourschedule`,
});

/** Section utilities for PDF generation */
export const sectionUtils = {
  /** Gets sections that should be excluded from the PDF */
  getHidden: (sections?: SurveySection[]): Set<string> =>
    new Set(sections?.filter((s) => s.isHidden).map((s) => s.name) || []),

  /** Gets user-added sections that should appear in the PDF */
  getCustom: (sections?: SurveySection[]): SurveySection[] =>
    sections?.filter(
      (s) => s.isCustom && !s.isHidden && s.pdfs?.[0]?.mediaUrl,
    ) || [],
};

// Backward compatibility exports
export const getHiddenSections = sectionUtils.getHidden;
export const getCustomSections = sectionUtils.getCustom;

/** Checks if uploaded media is a PDF file for direct embedding */
export const isPdfFile = (file: PdfMediaFile): boolean =>
  file.mediaType === "application/pdf" ||
  file.displayName?.toLowerCase().endsWith(".pdf") ||
  file.keyName?.toLowerCase().endsWith(".pdf") ||
  false;

/** Creates sections for property media (images, floor plans, flyers) */
export const generatePropertyMediaSections = (
  survey: PdfGenerationOptions["survey"],
  domain: string,
  surveyId: string,
  propertyId: string,
  mediaFiles: {
    images?: MediaFile[];
    floorPlans?: MediaFile[];
    flyers?: MediaFile[];
  },
): PdfSection[] => {
  const sections: PdfSection[] = [];

  // Generate floor plan sections
  mediaFiles.floorPlans?.forEach((floorPlan, index) => {
    if (!floorPlan || floorPlan.isHidden || !survey?.isIncludeSitePlans) return;
    try {
      const link =
        isPdfFile(floorPlan) && floorPlan.cloudfrontUrl
          ? floorPlan.cloudfrontUrl
          : `${domain}/${surveyId}/preview/properties/${propertyId}/floorPlanImage/${index + 1}`;
      sections.push({ name: "property floorplan", link });
    } catch (_error) {
      console.error("Error processing floor plan for property");
      // Continue without this floor plan rather than failing entire PDF generation
    }
  });

  // Generate flyer sections - filter out source PDFs if modified versions exist
  if (mediaFiles.flyers && survey?.isIncludeFlyers) {
    try {
      // Create a set of source media IDs that have been modified
      const modifiedMediaIds = new Set(
        mediaFiles.flyers
          .filter((file) => file?.modifiedFromMediaId)
          .map((file) => file.modifiedFromMediaId),
      );

      // Only include flyers that are not hidden, have a URL, and are not source PDFs
      mediaFiles.flyers.forEach((flyer) => {
        if (
          flyer &&
          !flyer.isHidden &&
          flyer.cloudfrontUrl &&
          flyer.mediaId &&
          !modifiedMediaIds.has(flyer.mediaId)
        ) {
          sections.push({ name: "property flyer", link: flyer.cloudfrontUrl });
        }
      });
    } catch (_error) {
      console.error("Error processing flyers for property");
      // Continue without flyers rather than failing entire PDF generation
    }
  }

  return sections;
};

/** Builds complete list of sections to include in the generated PDF */
export const generateSectionInfoList = async (
  options: PdfGenerationOptions,
): Promise<PdfSection[]> => {
  const { surveyId, baseUrl, mediaFiles, survey, uploadMarketMapFile } =
    options;
  let domain = baseUrl || window.location.origin;

  if (!survey?.sections) return [];

  // Sort sections by order property
  const sortedSections = [...survey.sections].sort((a, b) => a.order - b.order);

  const sections: PdfSection[] = [];
  const baseSections = createBaseSections(domain, surveyId);

  // Process sections in order using for...of to properly handle async operations
  for (const section of sortedSections) {
    // Skip hidden sections
    if (section.isHidden) continue;

    const sectionNameLower = section.name.toLowerCase();

    // Handle base sections by matching the name
    if (sectionNameLower === "cover") {
      sections.push({ name: section.name, link: baseSections.cover });
    } else if (
      sectionNameLower.includes("property") &&
      sectionNameLower.includes("comparison")
    ) {
      sections.push({
        name: section.name,
        link: baseSections["property comparison"],
      });
    } else if (
      sectionNameLower.includes("lease") &&
      sectionNameLower.includes("comp")
    ) {
      sections.push({
        name: section.name,
        link: baseSections["lease comps comparison"],
      });
    } else if (
      sectionNameLower.includes("market") &&
      sectionNameLower.includes("map")
    ) {
      if (uploadMarketMapFile) {
        try {
          const marketMapImagePayload = await marketMapGeneration(survey);
          await uploadMarketMapFile(marketMapImagePayload);
        } catch (_error) {
          console.error("Failed to upload market map after 3 retries");
          const marketMapError: Error & { isMarketMapError?: boolean } =
            new Error(PDF_GENERATION_ERROR_MESSAGE);
          marketMapError.isMarketMapError = true;
          throw marketMapError;
        }
      }
      sections.push({ name: section.name, link: baseSections["market map"] });
    } else if (sectionNameLower.includes("tour")) {
      sections.push({
        name: section.name,
        link: baseSections["tour schedule"],
      });
    } else if (sectionNameLower === "properties") {
      // Add property sections only if not hidden
      if (mediaFiles && survey.properties?.length) {
        // Use shared property filtering utility with showCurrentLocationPD flag
        const visibleProperties = getVisibleProperties(
          survey.properties,
          survey,
          undefined,
          true,
        );

        for (const { propertyId, name } of visibleProperties) {
          const propertyMedia = mediaFiles[propertyId];
          // Property main page
          sections.push({
            name: name,
            link: `${domain}/${surveyId}/preview/properties/${propertyId}`,
          });

          // Property media (images, floor plans, flyers)
          sections.push(
            ...generatePropertyMediaSections(
              survey,
              domain,
              surveyId,
              propertyId,
              {
                images: propertyMedia?.images,
                floorPlans: propertyMedia?.floorPlans,
                flyers: propertyMedia?.fliers,
              },
            ),
          );
        }
      }
    } else if (section.isCustom && section.pdfs?.[0]?.mediaUrl) {
      // Add custom sections
      sections.push({
        name: "section flyer",
        link: section.pdfs[0].mediaUrl,
      });
    }
  }

  return sections;
};

/** Extracts PDF information from a given item */
export const extractPdfInfo = (pdfItem: unknown) => {
  const item = pdfItem as Record<string, unknown> | null | undefined;
  return {
    fileUrl: typeof item?.fileUrl === "string" ? item.fileUrl : null,
    createdOn: typeof item?.createdOn === "string" ? item.createdOn : null,
  };
};

/** Identifies a PDF record so a stale (pre-regeneration) one can be detected */
const getPdfSignature = (pdf: PdfFileInfo | undefined): string | null =>
  pdf
    ? [pdf.id, pdf.createdOn, pdf.updatedOn, pdf.state, pdf.fileUrl].join("|")
    : null;

/**
 * Manages PDF generation and sharing workflow for surveys
 *
 * Workflow:
 * 1. Detects when PDF needs to be generated or updated
 * 2. Handles PDF generation with section content
 * 3. Provides ready-to-use PDF URL for sharing
 * 4. Automatically regenerates when survey changes
 *
 * Status progression: idle → loading → generating → available → outdated (cycle repeats)
 */
export const useSurveyPDF = ({
  surveyId,
  surveyUpdatedOn,
  isOpen,
  mediaFiles,
  survey,
  shouldPreventAutoTriggerRef,
}: UseSurveyPDFOptions): UseSurveyPDFReturn => {
  const [status, setStatus] = useState<PdfStatus>("idle");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfCreatedOn, setPdfCreatedOn] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const generatePdfCount = useRef(0);
  const lastSurveyUpdate = useRef<string | undefined>(null);
  // Signature of the PDF that existed before a manual (re)generation was started
  const staleSignatureRef = useRef<string | null>(null);
  const awaitingNewPdfRef = useRef(false);

  // Calculate polling interval based on status
  const pollingInterval = status === "loading" ? 5000 : 0;

  // Set up PDF generation and fetching capabilities
  const [generatePdf] = useGeneratePdfMutation();
  const [uploadMarketMapFile] = useUploadMarketMapFileMutation();
  const {
    data: pdfResponse,
    isLoading: isGettingPdf,
    refetch: refetchPdf,
  } = useGetPdfUrlQuery(
    isOpen ? { surveyId } : skipToken,
    {
      pollingInterval,
      refetchOnMountOrArgChange: true,
    },
  );

  const handlePdfAction = (forceGenerate = false) => {
    if (
      survey?.id &&
      (forceGenerate ||
        status === "idle" ||
        status === "outdated" ||
        status === "error")
    ) {
      // Check if the Properties section is visible (not hidden)
      const propertiesSection = survey.sections?.find(
        (s) => s.name?.toLowerCase() === "properties",
      );
      const isPropertiesSectionVisible =
        propertiesSection && !propertiesSection.isHidden;

      // mediaFiles is only required when the Properties section is visible
      if (!survey || (isPropertiesSectionVisible && !mediaFiles)) {
        setStatus("error");
        return;
      }

      // Remember the current PDF so it is not mistaken for the new one
      staleSignatureRef.current = getPdfSignature(pdfResponse?.[0]);
      awaitingNewPdfRef.current = true;

      // Increment counter immediately to prevent auto-trigger from firing
      generatePdfCount.current = 1;
      setStatus("generating");
      setErrorMessage(null);

      // Find section indices safely
      const marketMapIndex =
        survey.sections?.findIndex((s) =>
          s.name?.toLowerCase().includes("marketmap"),
        ) ?? -1;
      const propertyComparisonIndex =
        survey.sections?.findIndex((s) => {
          const name = s.name?.toLowerCase() || "";
          return name.includes("property") && name.includes("comparison");
        }) ?? -1;
      const leaseCompsIndex =
        survey.sections?.findIndex((s) => {
          const name = s.name?.toLowerCase() || "";
          return name.includes("lease") && name.includes("comp");
        }) ?? -1;
      const propertyIndex =
        survey.sections?.findIndex((s) =>
          s.name?.toLowerCase().includes("properties"),
        ) ?? -1;
      const tourScheduleIndex =
        survey.sections?.findIndex((s) =>
          s.name?.toLowerCase().includes("tour"),
        ) ?? -1;

      // Check if properties have custom fields
      const hasCustomFields =
        survey.properties?.some(
          (property) =>
            property.customFields && property.customFields.length > 0,
        ) ?? false;

      // Get active property list
      const activePropertyList =
        survey.properties?.filter((property) => !property.isHidden).length ?? 0;

      Analytics.send({
        event: "generatePDFClicked",
        SurveyID: survey.id,
        SurveyName: survey.name || "",
        Cover: survey.sections?.[0] ? !survey.sections[0].isHidden : false,
        BackgroundImage: survey.coverBackgroundImage?.displayName || "",
        SurveyTitle: survey.coverTitle || "",
        SurveySubtitle: survey.coverSubTitle || "",
        ClientName: survey.clientName || "",
        MarketMap:
          marketMapIndex >= 0
            ? !survey.sections[marketMapIndex].isHidden
            : false,
        PropertyComparison:
          propertyComparisonIndex >= 0
            ? !survey.sections[propertyComparisonIndex].isHidden
            : false,
        LeaseComps:
          leaseCompsIndex >= 0
            ? !survey.sections[leaseCompsIndex].isHidden
            : false,
        TourSchedule:
          tourScheduleIndex >= 0
            ? !survey.sections[tourScheduleIndex].isHidden
            : false,
        Properties:
          propertyIndex >= 0 ? !survey.sections[propertyIndex].isHidden : false,
        NumberOfProperties: survey.properties?.length || 0,
        PropertyCustomFields: hasCustomFields,
        ProjectID: survey.projectId || "",
        TeamID: survey.teamId || "",
        SurveyCreationDate: survey.createdOn || "",
        Origin: survey.origin || "",
        ProjectCreationDate: survey.projectCreatedAt || "",
        SiteIQUserID: survey.createdBy || "", // Use createdBy instead of user.userIdList
        CustomLegends: survey.customLegends || "",
        Collaborator: survey.isCollaborator || false,
        SubjectProperties: activePropertyList,
      });

      // Generate section list and PDF (await the async operation)
      return generateSectionInfoList({
        surveyId,
        baseUrl: window.location.origin,
        mediaFiles,
        survey,
        uploadMarketMapFile: (formData: FormData) =>
          uploadMarketMapFile(formData)
            .unwrap()
            .then((data) => ({ data })),
      })
        .then((sectionInfoList) =>
          generatePdf({
            surveyId,
            isLandscape: survey.pdfPaperSize === "Landscape",
            name: survey.name || "Survey PDF",
            sectionInfoList,
          }).unwrap(),
        )
        .then(() => {
          setStatus("loading");
          // Fetch the latest generated PDF right away; polling continues after
          refetchPdf();
        })
        .catch((error) => {
          awaitingNewPdfRef.current = false;
          console.error("PDF generation failed");
          if (error?.isMarketMapError) {
            setErrorMessage(error.message);
          }
          setStatus("error");
        });
    }
  };

  // useEffectEvent for handling outdated PDFs - stable function with fresh values
  const handleOutdatedPdf = useEffectEvent(() => {
    // Only trigger if PDF is outdated and we haven't generated yet (or survey was updated)
    if (status === "outdated" && generatePdfCount.current === 0) {
      generatePdfCount.current = 1;
      handlePdfAction();
    }
  });

  // useEffectEvent for initial PDF check - stable function with fresh values
  const handleInitialPdfCheck = useEffectEvent(() => {
    // Only set to loading if modal is opened AND we have a PDF response to check
    // This prevents infinite loops when pdfResponse is empty
    if (isOpen && status === "idle" && pdfResponse && pdfResponse.length > 0) {
      setStatus("loading");
    }
  });

  // Handle PDF availability updates using useEffectEvent
  const handlePdfAvailability = useEffectEvent(() => {
    if (pdfResponse && surveyUpdatedOn) {
      // No PDF exists, set status to idle so user can manually generate
      // But don't reset if we're currently generating, loading, or in error state
      if (pdfResponse.length === 0) {
        if (
          status !== "generating" &&
          status !== "loading" &&
          status !== "error"
        ) {
          setStatus("idle");
          setPdfUrl(null);
          setPdfCreatedOn(null);
        }
        return;
      }

      if (pdfResponse.length > 0) {
        const pdfInfo = pdfResponse[0];
        const pdfState = pdfInfo.state;

        // Ignore the previous PDF until the newly requested one shows up
        if (awaitingNewPdfRef.current) {
          if (getPdfSignature(pdfInfo) === staleSignatureRef.current) {
            return;
          }
          awaitingNewPdfRef.current = false;
        }

        // Check if PDF generation failed (state 4 or 5)
        if (pdfState === 4 || pdfState === 5) {
          setStatus("error");
          setPdfUrl(null);
          setPdfCreatedOn(null);
          return;
        }

        // Check if PDF is still generating (state < 3)
        if (pdfState < 3) {
          if (status !== "generating" && status !== "loading") {
            setStatus("loading");
          }
          return;
        }

        // PDF is ready (state === 3)
        if (pdfState === 3 && pdfInfo.fileUrl && status !== "generating") {
          const pdfCreatedOnDate = dayjs(normalizeDate(pdfInfo.createdOn));
          const surveyUpdated = dayjs(normalizeDate(surveyUpdatedOn));

          // Check if PDF is up-to-date or outdated
          if (!surveyUpdated.isAfter(pdfCreatedOnDate)) {
            setPdfUrl(pdfInfo.fileUrl);
            setPdfCreatedOn(pdfInfo.createdOn);
            setStatus("available");
          } else if (generatePdfCount.current === 0) {
            setPdfUrl(pdfInfo.fileUrl);
            setPdfCreatedOn(pdfInfo.createdOn);
            setStatus("outdated");
          }
        }
      }
    }
  });

  // Consolidated PDF management effects
  useEffect(() => {
    // Update survey counter when survey changes
    if (lastSurveyUpdate.current !== surveyUpdatedOn) {
      generatePdfCount.current = 0;
      lastSurveyUpdate.current = surveyUpdatedOn;
    }
    handleInitialPdfCheck();
    handlePdfAvailability();
  }, [pdfResponse, status, isOpen, isGettingPdf, surveyUpdatedOn]);

  // Handle auto-trigger for outdated PDFs with 3 second delay
  // Skip auto-trigger if shouldPreventAutoTriggerRef is set to true
  useEffect(() => {
    if (
      status === "outdated" &&
      generatePdfCount.current === 0 &&
      !shouldPreventAutoTriggerRef?.current
    ) {
      const timeoutId = setTimeout(() => {
        handleOutdatedPdf();
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [status, shouldPreventAutoTriggerRef]);

  // Cleanup effect when modal closes
  useEffect(() => {
    return () => {
      if (!isOpen) {
        awaitingNewPdfRef.current = false;
        setStatus("idle");
        setPdfUrl(null);
        setPdfCreatedOn(null);
        setErrorMessage(null);
      }
    };
  }, [isOpen]);

  return {
    status,
    pdfUrl,
    pdfCreatedOn,
    errorMessage,
    handlePdfAction,
    // A PDF is available to copy/share as soon as a link exists, even if outdated
    isPdfDownloadReady: !!pdfUrl,
  };
};
